import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    Line
} from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend
} from "chart.js";
import { FaArrowLeftLong, FaArrowRightLong } from "react-icons/fa6";
import { formatSalesValue } from "../../utils/formatUtils";
import { formatDisplayDate } from "../../utils/formateDateUtils";
import { MdAutorenew, MdError } from "react-icons/md";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend
);

const formatDate = (d) => d.toLocaleDateString('en-CA');

const addDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
};

const WeeklyPurchaseChart = ({ baseUrl, token, userID }) => {
    const today = new Date();

    const [endDate, setEndDate] = useState(today);
    const [startDate, setStartDate] = useState(addDays(today, -13));

    const [chartData, setChartData] = useState(null);
    const [rawData, setRawData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Generate colors for outlets
    const generateColors = (outlets) => {
        const colors = [
            "#FF6B6B", "#4ECDC4", "#FFD166", "#06D6A0", "#118AB2",
            "#073B4C", "#EF476F", "#7209B7", "#3A86FF", "#FB5607",
            "#8338EC", "#FF006E", "#FF9E00", "#1B9AAA", "#E63946"
        ];

        const colorMap = {};
        outlets.forEach((outlet, idx) => {
            colorMap[outlet] = colors[idx % colors.length];
        });
        return colorMap;
    };

    // ---------- Fetch ----------
    const fetchWeeklyPurchase = async () => {
        try {
            setLoading(true);

            const payload = {
                outlet_name: "ALL",
                start_date: formatDate(startDate),
                end_date: formatDate(endDate),
                token: token,
                employee_id: Number(userID)
            };

            const res = await axios.post(
                `${baseUrl}/getweeklypurchase_sales`,
                payload
            );

            const data = res.data;
            setRawData(data);

            const labels = data.days.map(d => d.date);
            const outlets = data.outlets || [];

            // Generate colors for outlets
            const colors = generateColors(outlets);

            // build dataset per outlet
            const datasets = outlets.map((outlet, idx) => {
                return {
                    label: outlet,
                    data: data.days.map(day => day.outlets[outlet]?.purchase || 0),
                    tension: 0.3,
                    borderColor: colors[outlet],
                    backgroundColor: colors[outlet],
                    pointBackgroundColor: colors[outlet],
                    pointBorderColor: "#ffffff",
                    pointBorderWidth: 2,
                    pointRadius: 4,
                    pointHoverRadius: 6,
                };
            });

            setChartData({
                labels,
                datasets
            });

        } catch (err) {
            console.error(err);
            setError(err.response?.data?.error ||
                err.response?.data?.message ||
                err.message ||
                "Failed to fetch purchase dataset.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWeeklyPurchase();
    }, [startDate, endDate]);

    // ---------- Navigation ----------
    const RANGE_DAYS = 14;

    const goPrevWeek = () => {
        const newEnd = addDays(startDate, -1);
        const newStart = addDays(newEnd, -(RANGE_DAYS - 1));
        setStartDate(newStart);
        setEndDate(newEnd);
    };

    const goNextWeek = () => {
        const newStart = addDays(endDate, 1);
        const newEnd = addDays(newStart, RANGE_DAYS - 1);

        if (newEnd > today) return;

        setStartDate(newStart);
        setEndDate(newEnd);
    };

    const isCurrentWeek = formatDate(endDate) === formatDate(today);

    // ---------- Custom tooltip ----------
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "bottom",
            },
            tooltip: {
                mode: 'index',
                intersect: false,
                displayColors: true,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleFont: {
                    size: 14,
                    weight: 'bold'
                },
                bodyFont: {
                    size: 13
                },
                padding: 12,

                callbacks: {
                    title: function (context) {
                        return context[0].label;
                    },

                    // This shows text per outlet
                    label: function (context) {
                        return `${context.dataset.label}: ${formatSalesValue(context.raw)}`;
                    },

                    // This controls the color box
                    labelColor: function (context) {
                        return {
                            borderColor: context.dataset.borderColor,
                            backgroundColor: context.dataset.borderColor,
                            borderWidth: 2,
                            borderRadius: 2
                        };
                    },

                    footer: function () {
                        return '';
                    }
                }
            },
            datalabels: {
                display: false,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'Sales Amount'
                }
            },
            x: {
                title: {
                    display: true,
                    text: 'Date'
                },
                ticks: {
                    callback: function (value) {
                        const label = this.getLabelForValue(value);
                        return formatDisplayDate(label);
                    }
                }
            }
        },
        // Make points always visible on hover
        interaction: {
            mode: 'index',
            intersect: false,
        },
        // Show points always
        elements: {
            point: {
                hoverRadius: 8,
                hoverBorderWidth: 3,
            }
        },
        // Enable hover for entire dataset
        hover: {
            mode: 'index',
            intersect: false
        }
    };

    const sortedDays = rawData?.days
        ? [...rawData.days].sort(
            (a, b) => new Date(b.date) - new Date(a.date)
        )
        : [];

    return (
        <div className="bg-white rounded-2xl shadow-lg p-4 max-w-6xl mx-auto mb-3">
            <h2 className="text-lg font-semibold text-center">
                Puchase Trend (14days)
            </h2>

            <div className="flex items-center justify-between mb-4 px-8">
                {/* Date Range */}
                <div className="text-sm font-semibold text-gray-500">
                    {formatDate(startDate)} → {formatDate(endDate)}
                </div>

                <div className="flex items-center gap-3 text-gray-500 hover:text-gray-600">
                    <button
                        onClick={goPrevWeek}
                        className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200"
                    >
                        <FaArrowLeftLong />
                    </button>

                    <button
                        disabled={isCurrentWeek}
                        onClick={goNextWeek}
                        className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-300 disabled:cursor-not-allowed"
                    >
                        <FaArrowRightLong />
                    </button>
                </div>
            </div>

            {/* Chart */}
            {loading && (
                <div className="flex gap-3 justify-center mt-8">
                    <MdAutorenew className="h-5 w-5 text-blue-500 animate-spin" />
                    <div className="text-blue-700">
                        Fetching cost center data...
                    </div>
                </div>
            )}

            {error && (
                <div className="flex gap-3 justify-center mt-8">
                    <MdError className="h-5 w-5 text-red-400" />
                    <div className="text-red-700">{error}</div>
                </div>
            )}

            <div className="w-full h-[420px] mx-auto">
                {!loading && !error && chartData && (
                    <Line data={chartData} options={options} className="w-full h-full mx-auto" />
                )}
            </div>

            {/* Sales Table */}
            {!loading && rawData && (
                <div className="mt-6 overflow-x-auto">
                    <table className="min-w-full border border-gray-200 text-sm text-center">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="p-2 font-semibold">
                                    Date
                                </th>

                                {rawData.outlets.map((outlet, index) => (
                                    <th
                                        key={index}
                                        colSpan={2}
                                        className="border p-2 text-center font-semibold"
                                    >
                                        {outlet}
                                    </th>
                                ))}
                            </tr>
                            <tr className="bg-gray-50">
                                <th className="p-2"></th>

                                {rawData.outlets.map((_, i) => (
                                    <React.Fragment key={`header-${i}`}>
                                        <th className="border p-2 text-center font-semibold">Purchase</th>
                                        <th className="border p-2 text-center font-semibold">Sales</th>
                                    </React.Fragment>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {sortedDays.map((day, rowIndex) => (
                                <tr key={rowIndex} className="hover:bg-gray-50">
                                    <td className="border p-2 font-semibold">
                                        {formatDisplayDate(day.date)}
                                    </td>

                                    {rawData.outlets.map((outlet, colIndex) => (
                                        <React.Fragment key={`row-${rowIndex}-${colIndex}`}>
                                            <td className="border p-2">
                                                {formatSalesValue(day.outlets[outlet]?.purchase || 0)}
                                            </td>
                                            <td className="border p-2">
                                                {formatSalesValue(day.outlets[outlet]?.sales || 0)}
                                            </td>
                                        </React.Fragment>
                                    ))}
                                </tr>
                            ))}

                            <tr className="bg-gray-100 font-semibold">
                                <td className="border p-2">
                                    Total
                                </td>

                                {rawData.outlets.map((outlet, index) => (
                                    <React.Fragment key={`total-${index}`}>
                                        <td className="border p-2">
                                            {formatSalesValue(
                                                rawData.days.reduce((sum, day) => {
                                                    return sum + (day.outlets[outlet]?.purchase || 0);
                                                }, 0)
                                            )}
                                        </td>

                                        <td className="border p-2">
                                            {formatSalesValue(
                                                rawData.days.reduce((sum, day) => {
                                                    return sum + (day.outlets[outlet]?.sales || 0);
                                                }, 0)
                                            )}
                                        </td>
                                    </React.Fragment>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default WeeklyPurchaseChart